export interface CardEntry { id: string; primary: string; secondary: string }
export type MeasureText = (text: string, size: number, weight?: number) => number;
export interface CardTile {
  entry: CardEntry;
  x: number; y: number; width: number; height: number;
  primaryLines: string[]; secondaryLines: string[]; textHeight: number;
}
export interface CardPage {
  fontSize: number; secondarySize: number; textGap: number; tiles: CardTile[];
}
interface CardLayoutOptions { singlePage?: boolean }

export function wrapText(text: string, width: number, size: number, measure: MeasureText, weight = 400): string[] {
  const lines: string[] = [];
  let line = "";
  for (const word of text.trim().split(/\s+/)) {
    const candidate = line ? `${line} ${word}` : word;
    if (measure(candidate, size, weight) <= width) { line = candidate; continue; }
    if (line) { lines.push(line); line = ""; }
    for (const character of Array.from(word)) {
      if (line && measure(line + character, size, weight) > width) { lines.push(line); line = ""; }
      line += character;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function fitPage(entries: readonly CardEntry[], width: number, height: number, measure: MeasureText, compact = false, scale = 1): CardPage | null {
  const columnOptions = entries.length === 1 ? [1] : entries.length === 2 ? [2, 1] : [2, 3, 1];
  for (const baseSize of compact ? [14] : [28, 26, 24, 22, 20, 18, 16, 14]) {
    const fontSize = baseSize * scale;
    const secondarySize = Math.max(11, Math.min(15, Math.round(baseSize * .65))) * scale;
    const gap = (baseSize >= 22 ? 10 : 6) * scale;
    const textGap = 4 * scale;
    for (const columns of columnOptions) {
      const rows = Math.ceil(entries.length / columns);
      const tileWidth = (width - (columns - 1) * gap) / columns;
      const tileHeight = (height - (rows - 1) * gap) / rows;
      const tiles = entries.map((entry, index): CardTile => {
        const primaryLines = wrapText(entry.primary, tileWidth - 12 * scale, fontSize, measure, 700);
        const secondaryLines = wrapText(entry.secondary, tileWidth - 12 * scale, secondarySize, measure);
        return {
          entry, x: (index % columns) * (tileWidth + gap), y: Math.floor(index / columns) * (tileHeight + gap),
          width: tileWidth, height: tileHeight, primaryLines, secondaryLines,
          textHeight: primaryLines.length * fontSize * 1.2 + secondaryLines.length * secondarySize * 1.25 + textGap,
        };
      });
      if (tiles.every(tile => tile.textHeight + 10 * scale <= tile.height)) return { fontSize, secondarySize, textGap, tiles };
    }
  }
  return null;
}

export function createCardLayout(entries: readonly CardEntry[], width: number, height: number, measure: MeasureText, options: CardLayoutOptions = {}): CardPage[] {
  if (!entries.length) return [];
  const full = fitPage(entries, width, height, measure);
  if (full) return [full];
  if (options.singlePage) {
    // Find the largest common text/spacing scale that retains every complete label.
    // Wallet cards have a fixed footprint and must never produce continuation cards.
    let lower = 0, upper = 1;
    let fitted: CardPage | null = null;
    for (let attempt = 0; attempt < 24; attempt++) {
      const scale = (lower + upper) / 2;
      const candidate = fitPage(entries, width, height, measure, true, scale);
      if (candidate) { fitted = candidate; lower = scale; }
      else upper = scale;
    }
    if (!fitted) throw new Error("card_content_too_long");
    return [fitted];
  }
  const pages: CardPage[] = [];
  let offset = 0;
  while (offset < entries.length) {
    let count = 0;
    for (let end = offset + 1; end <= entries.length; end++) {
      if (!fitPage(entries.slice(offset, end), width, height, measure, true)) break;
      count = end - offset;
    }
    if (!count) throw new Error("card_content_too_long");
    pages.push(fitPage(entries.slice(offset, offset + count), width, height, measure)!);
    offset += count;
  }
  return pages;
}
